import { ReadonlyVec3 } from 'gl-matrix';
import { DomainContext } from '../../changes/DomainContext';
import { RoiId } from '../../changes/ids';
import { DicomColor } from '../../dicom/dicomColor';
import { DICOMApprovalStatus } from '../../dicom/dicomConstants';
import {
    RoiGenerationAlgorithmType,
    RoiIdentificationCodeSequence,
    RoiInterpretedType,
} from '../../dicom/dicomstructureset';
import {
    ReviewLockedError,
    StaleReferenceError,
} from '../../errors/DomainError';
import { Patient } from '../../patient/patient';
import { Roi } from '../../patient/roi';
import { StructureSet } from '../../patient/structureset';
import { domainTrace } from '../../diagnostics/domainTrace';

/** Lightweight identity passed from UI down to commands. */
export interface RoiRef {
    readonly id: RoiId;
}

export class RoiService {
    constructor(
        private readonly context: DomainContext,
        private readonly patient: Patient
    ) {}

    rename(ref: RoiRef, newName: string): void {
        domainTrace.service('RoiService.rename', { id: ref.id });
        this.context.tracker.command(() => this.resolve(ref).rename(newName));
    }

    setColor(ref: RoiRef, color: DicomColor): void {
        domainTrace.service('RoiService.setColor', { id: ref.id, color });
        this.context.tracker.command(() => this.resolve(ref).setColor(color));
    }

    approve(ref: RoiRef, reviewer: string): void {
        domainTrace.service('RoiService.approve', { id: ref.id });
        this.context.tracker.command(() => {
            const { roi, structureSet } = this.resolveWithParent(ref);
            this.assertReviewable(structureSet);
            roi.approve(reviewer);
        });
    }

    reject(ref: RoiRef, reviewer: string): void {
        domainTrace.service('RoiService.reject', { id: ref.id });
        this.context.tracker.command(() => {
            const { roi, structureSet } = this.resolveWithParent(ref);
            this.assertReviewable(structureSet);
            roi.reject(reviewer);
        });
    }

    unapprove(ref: RoiRef): void {
        domainTrace.service('RoiService.unapprove', { id: ref.id });
        this.context.tracker.command(() => {
            const { roi, structureSet } = this.resolveWithParent(ref);
            this.assertReviewable(structureSet);
            roi.unapprove();
        });
    }

    setInterpretedType(ref: RoiRef, type: RoiInterpretedType): void {
        domainTrace.service('RoiService.setInterpretedType', {
            id: ref.id,
            type,
        });
        this.context.tracker.command(() => {
            const { roi, structureSet } = this.resolveWithParent(ref);
            // Single-EXTERNAL invariant: one authoritative guard for the
            // retype and create paths alike. Excluding the target ROI keeps
            // re-asserting EXTERNAL on the current EXTERNAL legal.
            structureSet.ensureExternalUnique(type, roi.id);
            roi.setInterpretedType(type);
        });
    }

    setVisibility(ref: RoiRef, visible: boolean): void {
        domainTrace.service('RoiService.setVisibility', {
            id: ref.id,
            visible,
        });
        this.context.tracker.command(() =>
            this.resolve(ref).setVisibility(visible)
        );
    }

    setLocked(ref: RoiRef, locked: boolean): void {
        domainTrace.service('RoiService.setLocked', { id: ref.id, locked });
        this.context.tracker.command(() => this.resolve(ref).setLocked(locked));
    }

    setContourThickness(ref: RoiRef, thickness: number): void {
        domainTrace.service('RoiService.setContourThickness', {
            id: ref.id,
            thickness,
        });
        this.context.tracker.command(() =>
            this.resolve(ref).setContourThickness(thickness)
        );
    }

    setDescription(ref: RoiRef, description: string): void {
        domainTrace.service('RoiService.setDescription', { id: ref.id });
        this.context.tracker.command(() =>
            this.resolve(ref).setDescription(description)
        );
    }

    setGenerationAlgorithm(
        ref: RoiRef,
        algorithm: RoiGenerationAlgorithmType
    ): void {
        domainTrace.service('RoiService.setGenerationAlgorithm', {
            id: ref.id,
            algorithm,
        });
        this.context.tracker.command(() =>
            this.resolve(ref).setGenerationAlgorithm(algorithm)
        );
    }

    setIdentificationCode(
        ref: RoiRef,
        code: RoiIdentificationCodeSequence
    ): void {
        domainTrace.service('RoiService.setIdentificationCode', {
            id: ref.id,
            code: code.codeValue,
        });
        this.context.tracker.command(() =>
            this.resolve(ref).setIdentificationCode(code)
        );
    }

    setElectronDensityOverride(ref: RoiRef, value: number | undefined): void {
        domainTrace.service('RoiService.setElectronDensityOverride', {
            id: ref.id,
            value,
        });
        this.context.tracker.command(() =>
            this.resolve(ref).setElectronDensityOverride(value)
        );
    }

    setMassDensityOverride(ref: RoiRef, value: number | undefined): void {
        domainTrace.service('RoiService.setMassDensityOverride', {
            id: ref.id,
            value,
        });
        this.context.tracker.command(() =>
            this.resolve(ref).setMassDensityOverride(value)
        );
    }

    setPointCoordinates(ref: RoiRef, coordinatesMm: ReadonlyVec3): void {
        domainTrace.service('RoiService.setPointCoordinates', { id: ref.id });
        this.context.tracker.command(() =>
            this.resolve(ref).setPointCoordinates(coordinatesMm)
        );
    }

    /**
     * Per-ROI review (approve/reject/unapprove) is allowed only while the
     * structure set is UNAPPROVED. Once the set is signed off (APPROVED or
     * REJECTED) it is locked — DICOM carries a single set-level approval
     * status, so a signed-off set with a disagreeing ROI is not
     * representable, and revising it must start from an explicit set-level
     * unapprove. See the revised design §2.9.
     */
    private assertReviewable(structureSet: StructureSet): void {
        if (structureSet.approvalStatus !== DICOMApprovalStatus.UNAPPROVED) {
            throw new ReviewLockedError(
                `Cannot review structures in a signed-off structure set ` +
                    `'${structureSet.structureSetLabel}'. Unapprove the ` +
                    `structure set first to re-review its structures.`
            );
        }
    }

    private resolve(ref: RoiRef): Roi {
        return this.resolveWithParent(ref).roi;
    }

    private resolveWithParent(ref: RoiRef): {
        roi: Roi;
        structureSet: StructureSet;
    } {
        for (const ss of this.patient.structureSets) {
            const roi = ss.getRoiById(ref.id);
            if (roi) {
                return { roi, structureSet: ss };
            }
        }
        throw new StaleReferenceError(
            `Roi ${ref.id} no longer exists. It may have been deleted.`
        );
    }
}
